import { useEffect, useState } from "react";
import Header from "../../../components/ui/Header";
import api from "../../../api/axiosInstance";

export default function PreparationGuide() {
  const userId = "demo-user"; // replace with real user id later

  const [category, setCategory] = useState("technical");
  const [topics, setTopics] = useState([]);
  const [topic, setTopic] = useState("");

  const [session, setSession] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0); // Force refresh after reset

  const [loadingTopics, setLoadingTopics] = useState(false);
  const [loadingQuestions, setLoadingQuestions] = useState(false);

  /* ================= FETCH TOPICS ================= */
  useEffect(() => {
    let isMounted = true;

    setLoadingTopics(true);
    setTopics([]);
    setTopic("");
    setSession(null);
    setQuestions([]);
    setCurrentIndex(0);

    const fetchTopics = async () => {
      try {
        const res = await api.get("/api/questiondatabase/topics", {
          params: { category },
        });

        const safe = Array.isArray(res.data) ? res.data : [];
        if (isMounted) {
          setTopics(safe);
          if (safe.length > 0) setTopic(safe[0]);
        }
      } catch (err) {
        console.error("Failed to load topics", err);
      } finally {
        if (isMounted) setLoadingTopics(false);
      }
    };

    fetchTopics();
    return () => (isMounted = false);
  }, [category]);

  /* ================= SESSION ================= */
  useEffect(() => {
    if (!topic) return;

    const createSession = async () => {
      try {
        const res = await api.post("/api/preparation/session", {
          userId,
          category,
          topic,
        });
        setSession(res.data);
      } catch (err) {
        console.error("Failed to create session", err);
      }
    };

    createSession();
  }, [topic, category]);

  /* ================= QUESTIONS ================= */
  useEffect(() => {
    if (!session) return;

    let isMounted = true;
    setLoadingQuestions(true);

    const fetchQuestions = async () => {
      try {
        console.log(`Fetching questions for session ${session._id} with refreshKey ${refreshKey}`);
        const res = await api.get(
          `/api/preparation/questions/${session._id}?t=${refreshKey}` // Add timestamp to prevent caching
        );

        const safe = Array.isArray(res.data) ? res.data : [];
        console.log("Fetched questions:", safe.map(q => ({ id: q._id, userAnswer: q.userAnswer, isCorrect: q.isCorrect })));
        
        if (isMounted) {
          setQuestions(safe);

          const firstUnanswered = safe.findIndex(
            (q) => !q.userAnswer
          );
          setCurrentIndex(firstUnanswered === -1 ? 0 : firstUnanswered);
        }
      } catch (err) {
        console.error("Failed to load questions", err);
      } finally {
        if (isMounted) setLoadingQuestions(false);
      }
    };

    fetchQuestions();
    return () => (isMounted = false);
  }, [session, refreshKey]); // Add refreshKey to dependency array

  const question = questions[currentIndex];

  /* ================= RESET TOPIC ANSWERS ================= */
  const resetTopicAnswers = async () => {
    if (!session || !topic) return;

    console.log(`Resetting answers for session ${session._id}, topic ${topic}`);
    
    try {
      // Reset answers on backend
      console.log("Making POST request to:", `/api/preparation/reset`);
      console.log("Request data:", { userId, sessionId: session._id });
      
      const resetResponse = await api.post("/api/preparation/reset", {
        userId,
        sessionId: session._id,
      });
      console.log("Reset response:", resetResponse.data);

      // Reset local state immediately
      const resetQuestions = questions.map(q => ({
        ...q,
        userAnswer: null,
        isCorrect: false,
      }));
      setQuestions(resetQuestions);
      setCurrentIndex(0);
      console.log("Questions after reset:", resetQuestions);

      // Force refresh to ensure backend state is synced
      setRefreshKey(prev => prev + 1);
      console.log("Refresh key incremented");
    } catch (err) {
      console.error("Failed to reset answers", err);
      // Still update local state even if backend fails
      const resetQuestions = questions.map(q => ({
        ...q,
        userAnswer: null,
        isCorrect: false,
      }));
      setQuestions(resetQuestions);
      setCurrentIndex(0);
      // Still force refresh
      setRefreshKey(prev => prev + 1);
    }
  };

  /* ================= ANSWER ================= */
  const handleAnswer = async (key) => {
    if (!question || question.userAnswer) return;

    try {
      await api.post("/api/preparation/answer", {
        userId,
        sessionId: session._id,
        questionId: question._id,
        selectedAnswer: key,
      });

      const updated = [...questions];
      updated[currentIndex] = {
        ...question,
        userAnswer: key,
        isCorrect: key === question.correctOption,
      };
      setQuestions(updated);
    } catch (err) {
      console.error("Failed to submit answer", err);
    }
  };

  /* ================= OPTIONS ================= */
  const options = question
    ? question.options && typeof question.options === "object"
      ? Object.entries(question.options).map(([key, value]) => ({
          key,
          value,
        }))
      : question.optionA
      ? [
          { key: "A", value: question.optionA },
          { key: "B", value: question.optionB },
          { key: "C", value: question.optionC },
          { key: "D", value: question.optionD },
        ]
      : []
    : [];

  return (
    <div className="min-h-screen bg-muted/40 pt-20">
      <Header />

      {/* BANNER */}
      <div className="bg-blue-50 border-b py-3 text-center font-semibold">
        📘 Preparation Mode — Instant answers & explanations
      </div>

      {/* CATEGORY */}
      <div className="flex justify-center gap-4 bg-white border-b py-3">
        {["aptitude", "technical"].map((cat) => (
          <button
            key={cat}
            onClick={() => setCategory(cat)}
            className={`px-6 py-2 rounded font-semibold
              ${
                category === cat
                  ? "bg-primary text-white"
                  : "border hover:bg-muted"
              }`}
          >
            {cat.toUpperCase()}
          </button>
        ))}
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-[260px_1fr_260px] gap-6 px-6 py-6">

        {/* TOPICS */}
        <aside className="bg-white border rounded-xl p-4">
          <h4 className="font-semibold mb-3">Topics</h4>
          {loadingTopics && (
            <p className="text-sm text-muted">Loading topics…</p>
          )}
          {topics.map((t) => (
            <button
              key={`${category}-${t}`}
              onClick={() => setTopic(t)}
              className={`w-full text-left px-3 py-2 rounded mb-1
                ${
                  topic === t
                    ? "bg-primary text-white"
                    : "hover:bg-muted"
                }`}
            >
              {t.replaceAll("_", " ").toUpperCase()}
            </button>
          ))}
        </aside>

        {/* QUESTION */}
        <main className="bg-white border rounded-xl p-6">
          {loadingQuestions ? (
            <p className="text-center text-muted">Loading questions…</p>
          ) : !question ? (
            <p className="text-center text-muted-foreground">
              No questions available
            </p>
          ) : (
            <>
              <p className="text-sm mb-2">
                Question {currentIndex + 1} of {questions.length}
              </p>

              <h2 className="text-lg font-semibold mb-5">
                {question.questionText}
              </h2>

              {options.map((opt) => {
                const selected = question.userAnswer === opt.key;
                const correct = question.correctOption === opt.key;

                return (
                  <button
                    key={`${question._id}-${opt.key}`}
                    onClick={() => handleAnswer(opt.key)}
                    disabled={!!question.userAnswer}
                    className={`w-full text-left px-4 py-3 mb-2 border rounded-lg
                      ${
                        question.userAnswer
                          ? correct
                            ? "bg-green-50 border-green-500"
                            : selected
                            ? "bg-red-50 border-red-500"
                            : "opacity-60"
                          : "hover:bg-muted"
                      }`}
                  >
                    <b>{opt.key}.</b> {opt.value}
                  </button>
                );
              })}

              {question.userAnswer && (
                <div className="mt-4 bg-muted p-4 rounded">
                  <p className="font-semibold">
                    Correct Answer: {question.correctOption}
                  </p>
                  <p className="text-sm mt-1">{question.explanation}</p>
                </div>
              )}

              {/* PREV / NEXT */}
              <div className="flex justify-between mt-6">
                <button
                  disabled={currentIndex === 0}
                  onClick={() => setCurrentIndex((i) => i - 1)}
                  className="border px-4 py-2 rounded disabled:opacity-40"
                >
                  ← Previous
                </button>

                <button
                  disabled={currentIndex === questions.length - 1}
                  onClick={() => setCurrentIndex((i) => i + 1)}
                  className="border px-4 py-2 rounded disabled:opacity-40"
                >
                  Next →
                </button>
              </div>
            </>
          )}
        </main>

        {/* PROGRESS */}
        <aside className="bg-white border rounded-xl p-4">
          <div className="flex justify-between items-center mb-3">
            <h4 className="font-semibold">Progress</h4>
            <button
              onClick={resetTopicAnswers}
              className="text-xs px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
            >
              Reset
            </button>
          </div>
          <div className="grid grid-cols-5 gap-2">
            {questions.map((q, i) => (
              <button
                key={q._id}
                onClick={() => setCurrentIndex(i)}
                className={`h-9 rounded text-sm
                  ${
                    q.userAnswer
                      ? q.isCorrect
                        ? "bg-green-500 text-white"
                        : "bg-red-500 text-white"
                      : i === currentIndex
                      ? "bg-primary text-white"
                      : "bg-muted"
                  }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}
